import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import { PermissionsContext } from "../../utils/PermissionsContext";
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Tooltip, InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import api from "../../utils/api";

/* ── Styles ─────────────────────────────────────────────────────────────── */
const primaryBtnSx = {
  backgroundColor: "#2f63ad", color: "#fff", fontWeight: 700, borderRadius: "10px",
  textTransform: "none", boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};
const secondaryBtnSx = { borderRadius: "10px", textTransform: "none", fontWeight: 600 };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } };
const dialogPaperSx = { borderRadius: "18px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" };

const RES_TYPES = ["transient", "monthly", "seasonal", "annual"];
const fmtDate = (iso) => { if (!iso) return "N/A"; return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };

/* ── Waitlist dialog ────────────────────────────────────────────────────── */
const BLANK = {
  owner_type: "external", owner_id: "", vessel_id: "",
  dock_id: "", min_length: "", max_length: "", reservation_type: "annual", notes: "",
};

const WaitlistDialog = ({ open, editing, marinaId, docks, onClose, onSaved }) => {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [owners, setOwners] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [vessels, setVessels] = useState([]);
  const [vesselSearch, setVesselSearch] = useState("");
  const ownerDebounce = useRef(null);
  const vesselDebounce = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...BLANK, ...editing } : BLANK);
      setErr(""); setOwnerSearch(""); setVesselSearch("");
    }
  }, [open, editing]);

  useEffect(() => {
    if (form.owner_type !== "external" || !ownerSearch.trim()) { setOwners([]); return; }
    clearTimeout(ownerDebounce.current);
    ownerDebounce.current = setTimeout(async () => {
      try { const r = await api.get(`/vessel-owners?search=${encodeURIComponent(ownerSearch)}`); setOwners(r.data || []); }
      catch { /* silent */ }
    }, 350);
    return () => clearTimeout(ownerDebounce.current);
  }, [ownerSearch, form.owner_type]);

  useEffect(() => {
    if (!vesselSearch.trim()) { setVessels([]); return; }
    clearTimeout(vesselDebounce.current);
    vesselDebounce.current = setTimeout(async () => {
      try { const r = await api.get(`/vessels?search=${encodeURIComponent(vesselSearch)}`); setVessels(r.data || []); }
      catch { /* silent */ }
    }, 350);
    return () => clearTimeout(vesselDebounce.current);
  }, [vesselSearch]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        marina_id: marinaId,
        owner_type: form.owner_type,
        owner_id: form.owner_type === "external" ? (form.owner_id || null) : null,
        vessel_id: form.vessel_id || null,
        dock_id: form.dock_id || null,
        min_length: form.min_length || null,
        max_length: form.max_length || null,
        reservation_type: form.reservation_type,
        notes: form.notes,
      };
      if (editing?.id) await api.put(`/marina-waitlist/${editing.id}`, payload);
      else await api.post("/marina-waitlist", payload);
      onSaved();
      onClose();
    } catch { setErr("Failed to save waitlist entry."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3, pb: 1 }}>{editing ? "Edit Waitlist Entry" : "Add to Waitlist"}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Owner Type</InputLabel>
              <Select value={form.owner_type} label="Owner Type" onChange={set("owner_type")}>
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="hoa_member">HOA Member</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {form.owner_type === "external" && (
            <Grid item xs={12}>
              <TextField fullWidth size="small" placeholder="Search external owner…" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }} sx={fieldSx} />
              {owners.length > 0 && !form.owner_id && (
                <Paper elevation={2} sx={{ mt: 0.5, borderRadius: "10px", overflow: "hidden", maxHeight: 140, overflowY: "auto" }}>
                  {owners.map((o) => (
                    <Box key={o.id} onClick={() => { setForm((p) => ({ ...p, owner_id: o.id })); setOwnerSearch(`${o.first_name} ${o.last_name}`); setOwners([]); }}
                      sx={{ px: 2, py: 1, cursor: "pointer", "&:hover": { backgroundColor: "rgba(47,99,173,0.06)" }, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.first_name} {o.last_name}. {o.email}</Typography>
                    </Box>
                  ))}
                </Paper>
              )}
              {form.owner_id && <Typography variant="caption" sx={{ color: "#16a34a", ml: 1 }}>✓ Owner selected</Typography>}
            </Grid>
          )}

          {/* Vessel */}
          <Grid item xs={12}>
            <TextField fullWidth size="small" placeholder="Search vessel (optional)…" value={vesselSearch} onChange={(e) => setVesselSearch(e.target.value)}
              label="Vessel" InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }} sx={fieldSx} InputLabelProps={{ shrink: true }} />
            {vessels.length > 0 && !form.vessel_id && (
              <Paper elevation={2} sx={{ mt: 0.5, borderRadius: "10px", overflow: "hidden", maxHeight: 140, overflowY: "auto" }}>
                {vessels.map((v) => (
                  <Box key={v.id} onClick={() => { setForm((p) => ({ ...p, vessel_id: v.id })); setVesselSearch(v.vessel_name); setVessels([]); }}
                    sx={{ px: 2, py: 1, cursor: "pointer", "&:hover": { backgroundColor: "rgba(47,99,173,0.06)" }, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.vessel_name}. {v.loa ? `${v.loa}ft LOA` : ""} {v.owner_display}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Preferred Dock</InputLabel>
              <Select value={form.dock_id} label="Preferred Dock" onChange={set("dock_id")}>
                <MenuItem value="">Any Dock</MenuItem>
                {docks.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Reservation Type</InputLabel>
              <Select value={form.reservation_type} label="Reservation Type" onChange={set("reservation_type")}>
                {RES_TYPES.map((t) => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Min Length (ft)" type="number" value={form.min_length} onChange={set("min_length")} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Max Length (ft)" type="number" value={form.max_length} onChange={set("max_length")} sx={fieldSx} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Notes" value={form.notes} onChange={set("notes")} sx={fieldSx} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Update" : "Add to Waitlist"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Main component ─────────────────────────────────────────────────────── */
const MarinaWaitlist = ({ marinaId, showSnack }) => {
  const perms = useContext(PermissionsContext);
  const canManage = perms.includes("marina.manage_reservations");

  const [entries, setEntries] = useState([]);
  const [docks, setDocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [repositioning, setRepositioning] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [wlRes, dockRes] = await Promise.all([
        api.get(`/marina-waitlist?marina_id=${marinaId}`),
        api.get(`/marina-docks?marina_id=${marinaId}`),
      ]);
      setEntries((wlRes.data || []).sort((a, b) => (a.position ?? 999) - (b.position ?? 999)));
      setDocks(dockRes.data || []);
    } catch { showSnack("Failed to load waitlist", "error"); }
    finally { setLoading(false); }
  }, [marinaId, showSnack]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const movePosition = async (entry, direction) => {
    const newPos = (entry.position ?? 1) + (direction === "up" ? -1 : 1);
    if (newPos < 1) return;
    setRepositioning(entry.id);
    try {
      await api.put(`/marina-waitlist/${entry.id}`, { ...entry, position: newPos });
      fetchData();
    } catch { showSnack("Failed to reorder", "error"); }
    finally { setRepositioning(null); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Remove from waitlist?")) return;
    try {
      await api.put(`/marina-waitlist/${id}`, { is_active: 0 });
      showSnack("Removed from waitlist");
      fetchData();
    } catch { showSnack("Failed to remove entry", "error"); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
            Waitlist
            {!loading && <Chip label={entries.length} size="small" sx={{ ml: 1, height: 20, fontWeight: 700, backgroundColor: "rgba(217,119,6,0.1)", color: "#d97706" }} />}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Manage slip requests in order of priority. Use arrows to reorder.
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx} onClick={() => setDialog({ open: true, editing: null })}>
            Add to Waitlist
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  {["Pos.", "Owner / Vessel", "Preferred Dock", "Size Range", "Type", "Date Added", "Actions"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a", py: 1.25, whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                      <HourglassTopIcon sx={{ fontSize: 40, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
                      <Typography variant="body2" sx={{ color: "#9aa5b4" }}>No waitlist entries.</Typography>
                    </TableCell>
                  </TableRow>
                ) : entries.map((e, idx) => (
                  <TableRow key={e.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: "8px", backgroundColor: "rgba(47,99,173,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#2f63ad" }}>{e.position ?? idx + 1}</Typography>
                        </Box>
                        {canManage && (
                          <Box>
                            <Tooltip title="Move up">
                              <span>
                                <IconButton size="small" disabled={idx === 0 || repositioning === e.id} onClick={() => movePosition(e, "up")} sx={{ p: 0.25 }}>
                                  <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move down">
                              <span>
                                <IconButton size="small" disabled={idx === entries.length - 1 || repositioning === e.id} onClick={() => movePosition(e, "down")} sx={{ p: 0.25 }}>
                                  <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>{e.owner_name || "N/A"}</Typography>
                      {e.vessel_name && <Typography variant="caption" sx={{ color: "#6b778c" }}>{e.vessel_name}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.82rem", color: "#445" }}>{e.dock_name || "Any"}</TableCell>
                    <TableCell sx={{ fontSize: "0.82rem", color: "#445" }}>
                      {e.min_length || e.max_length ? `${e.min_length ?? "N/A"}–${e.max_length ?? "N/A"} ft` : "Any"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={e.reservation_type?.charAt(0).toUpperCase() + e.reservation_type?.slice(1)}
                        size="small"
                        sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, backgroundColor: "rgba(47,99,173,0.08)", color: "#2f63ad" }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c" }}>{fmtDate(e.created_at)}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => setDialog({ open: true, editing: e })}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title="Remove from waitlist">
                          <IconButton size="small" color="error" onClick={() => handleRemove(e.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <WaitlistDialog
        open={dialog.open}
        editing={dialog.editing}
        marinaId={marinaId}
        docks={docks}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={() => { fetchData(); showSnack(dialog.editing ? "Waitlist updated" : "Added to waitlist"); }}
      />
    </Box>
  );
};

export default MarinaWaitlist;
